import React from 'react';

import './App.css';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import AdminDashboard from './pages/admin/dashboard/adminDash';
import TechDash from './pages/technician/techDash';
import QuoteGenerator from './pages/admin/quotes/QuoteGenerator';
import QuotesList from './pages/admin/quotes/QuotesList';
import WorkOrderForm from './pages/admin/work-orders/WorkOrderForm';
import WorkOrdersList from './pages/admin/work-orders/WorkOrdersList';
import WorkOrderFormTemplatesList from './pages/admin/work-order-forms/WorkOrderFormTemplatesList';
import WorkOrderFormBuilder from './pages/admin/work-order-forms/WorkOrderFormBuilder';
import InvoiceGenerator from './pages/admin/invoices/InvoiceGenerator';
import InvoicesList from './pages/admin/invoices/InvoicesList';
import ClientsPage from './pages/admin/clients/ClientsPage';
import NewClientPage from './pages/admin/clients/NewClientPage';
import DeviceDetailAdminPage from './pages/admin/devices/DeviceDetailAdminPage';
import SettingsPage from './pages/admin/settings/SettingsPage';
import PdfStudioPage from './pages/admin/pdf-studio/PdfStudioPage';
import TimeSheet from './pages/technician/TimeSheet/TimeSheet';
import JobList from './pages/technician/JobList/JobList';
import TechJobHub from './pages/technician/jobs/TechJobHub';
import JobLog from './pages/technician/JobLog/JobLog';
import Report from './pages/technician/ExportReport/Report';
import TechSettingsPage from './pages/technician/settings/TechSettingsPage';
import NewInspection from './pages/inspections/NewInspection';
import InspectionChecklist from './pages/inspections/InspectionChecklist';
import InspectionSummary from './pages/inspections/InspectionSummary';
import InspectionsList from './pages/inspections/InspectionsList';
import AdminInspectionDeviceChecklist from './pages/inspections/AdminInspectionDeviceChecklist';
import AdminInspectionFieldNotice from './pages/inspections/AdminInspectionFieldNotice';
import DeficienciesBoard from './pages/admin/deficiencies/DeficienciesBoard';
import { AuthProvider } from './auth/AuthContext';
import { FieldModeProvider } from './auth/FieldModeContext';
import { BrandingProvider } from './branding/BrandingContext';
import RequireAuth from './auth/RequireAuth';
import FieldModeGuard from './auth/FieldModeGuard';
import FieldModeTechSettingsRedirect from './auth/FieldModeTechSettingsRedirect';
import Login from './pages/login';
import Register from './pages/register';
import Signup from './pages/signup';
import ForcePasswordPage from './pages/account/ForcePasswordPage';
import InvitePage from './pages/invite/InvitePage';
import InviteExpiredPage from './pages/invite/InviteExpiredPage';

function AdminRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
    return <RequireAuth role="admin">{children}</RequireAuth>;
}

function TechnicianRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
    return (
        <RequireAuth role="technician">
            <FieldModeTechSettingsRedirect>{children}</FieldModeTechSettingsRedirect>
        </RequireAuth>
    );
}

function App(): React.JSX.Element {
    return (

        <AuthProvider>
            <FieldModeProvider>
            <BrandingProvider>
            <BrowserRouter>
                <FieldModeGuard>
                <Routes>
                    <Route path="/" element={<Navigate to="/login" replace />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/invite/expired" element={<InviteExpiredPage />} />
                    <Route path="/invite/:token" element={<InvitePage />} />
                    <Route path="/account/password" element={<RequireAuth><ForcePasswordPage /></RequireAuth>} />

                    <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                    <Route path="/admin/clients" element={<AdminRoute><ClientsPage /></AdminRoute>} />
                    <Route path="/admin/clients/new" element={<AdminRoute><NewClientPage /></AdminRoute>} />
                    <Route path="/admin/clients/:id" element={<AdminRoute><NewClientPage /></AdminRoute>} />
                    <Route path="/admin/devices/:id" element={<AdminRoute><DeviceDetailAdminPage /></AdminRoute>} />
                    <Route path="/admin/quotes" element={<AdminRoute><QuotesList /></AdminRoute>} />
                    <Route path="/admin/quotes/new" element={<AdminRoute><QuoteGenerator /></AdminRoute>} />
                    <Route path="/admin/work-orders" element={<AdminRoute><WorkOrdersList /></AdminRoute>} />
                    <Route path="/admin/work-order/new" element={<AdminRoute><WorkOrderForm /></AdminRoute>} />
                    <Route path="/admin/work-order-forms" element={<AdminRoute><WorkOrderFormTemplatesList /></AdminRoute>} />
                    <Route path="/admin/work-order-forms/new" element={<AdminRoute><WorkOrderFormBuilder /></AdminRoute>} />
                    <Route path="/admin/work-order-forms/:id" element={<AdminRoute><WorkOrderFormBuilder /></AdminRoute>} />
                    <Route path="/admin/invoices" element={<AdminRoute><InvoicesList /></AdminRoute>} />
                    <Route path="/admin/invoice/new" element={<AdminRoute><InvoiceGenerator /></AdminRoute>} />
                    <Route path="/admin/deficiencies" element={<AdminRoute><DeficienciesBoard /></AdminRoute>} />
                    <Route path="/admin/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
                    <Route path="/admin/pdf-studio" element={<AdminRoute><PdfStudioPage /></AdminRoute>} />
                    <Route path="/admin/inspections" element={<AdminRoute><InspectionsList /></AdminRoute>} />
                    <Route path="/admin/inspections/new" element={<AdminRoute><NewInspection /></AdminRoute>} />
                    <Route path="/admin/inspections/:id/field" element={<AdminRoute><AdminInspectionFieldNotice /></AdminRoute>} />
                    <Route path="/admin/inspections/:id/checklist" element={<AdminRoute><AdminInspectionDeviceChecklist /></AdminRoute>} />
                    <Route path="/admin/inspections/:id/summary" element={<AdminRoute><InspectionSummary /></AdminRoute>} />

                    <Route path="/technician/dashboard" element={<TechnicianRoute><TechDash /></TechnicianRoute>} />
                    <Route path="/technician/TimeSheet" element={<TechnicianRoute><TimeSheet /></TechnicianRoute>} />
                    <Route path="/technician/JobList" element={<TechnicianRoute><JobList /></TechnicianRoute>} />
                    <Route path="/technician/jobs/:workOrderId" element={<TechnicianRoute><TechJobHub /></TechnicianRoute>} />
                    <Route path="/technician/JobLog" element={<TechnicianRoute><JobLog /></TechnicianRoute>} />
                    <Route path="/technician/ExportReport" element={<TechnicianRoute><Report /></TechnicianRoute>} />
                    <Route path="/technician/settings" element={<TechnicianRoute><TechSettingsPage /></TechnicianRoute>} />
                    <Route path="/technician/inspections" element={<TechnicianRoute><InspectionsList /></TechnicianRoute>} />
                    <Route path="/technician/inspections/:id/checklist" element={<TechnicianRoute><InspectionChecklist /></TechnicianRoute>} />
                    <Route path="/technician/inspections/:id/summary" element={<TechnicianRoute><InspectionSummary /></TechnicianRoute>} />

                    <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
                </FieldModeGuard>
            </BrowserRouter>
            </BrandingProvider>
            </FieldModeProvider>
        </AuthProvider>

    );
}

export default App;
